import { useState, useEffect, useCallback } from 'react';
import { addDays, format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { calcNextDue, todayIT } from '../lib/recurring';
import type { Expense } from '../types';

export interface UseRecurringReturn {
  templates: Expense[];
  loading: boolean;
  logNow: (template: Expense) => Promise<void>;
  disableTemplate: (template: Expense) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useRecurring(): UseRecurringReturn {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    // Fetch recurring templates due within 30 days (including overdue)
    const thirtyDaysOut = format(addDays(new Date(), 30), 'yyyy-MM-dd');

    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_template', true)
      .not('next_due', 'is', null)
      .lte('next_due', thirtyDaysOut)
      .order('next_due', { ascending: true });

    if (!error) setTemplates((data as Expense[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Realtime: re-fetch on any change to expense templates for this user
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`recurring:${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expenses', filter: `user_id=eq.${user.id}` },
        () => fetchTemplates(),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchTemplates]);

  /** Log a template as of today, then advance its next_due */
  const logNow = async (template: Expense): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    const today = todayIT();

    // 1. Insert a logged (non-template) instance
    const { error: insertErr } = await supabase
      .from('expenses')
      .insert({
        user_id:      user.id,
        description:  template.description,
        amount:       template.amount,
        category:     template.category,
        recurring:    false,
        recurring_type: 'none',
        is_template:  false,
        template_id:  template.id,
        date:         today,
        next_due:     null,
        frequency_days: null,
      });
    if (insertErr) throw insertErr;

    // 2. Advance next_due on the master template
    const nextDue = calcNextDue(today, template.recurring_type, template.frequency_days);
    const { error: updateErr } = await supabase
      .from('expenses')
      .update({ next_due: nextDue })
      .eq('id', template.id)
      .eq('user_id', user.id);
    if (updateErr) throw updateErr;

    // Optimistic local update
    setTemplates(prev =>
      prev.map(t => t.id === template.id ? { ...t, next_due: nextDue } : t),
    );

    // Ensure widget reflects due-window changes immediately when realtime is delayed.
    await fetchTemplates();
  };

  /** Disable a recurring template and keep an expense history row intact */
  const disableTemplate = async (template: Expense): Promise<void> => {
    if (!user) throw new Error('Not authenticated');

    const { error: updateErr } = await supabase
      .from('expenses')
      .update({
        recurring: false,
        recurring_type: 'none',
        is_template: false,
        next_due: null,
        frequency_days: null,
      })
      .eq('id', template.id)
      .eq('user_id', user.id);

    if (updateErr) throw updateErr;
    await fetchTemplates();
  };

  return { templates, loading, logNow, disableTemplate, refetch: fetchTemplates };
}
